/* eslint-disable react/prop-types */
import { Drawer, IconButton, MenuItem, Select } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import dayjs from 'dayjs'
import React from 'react'

import { fetchApartmentEnrolments } from 'api/apartment'
// The same drawer styles the Parent Dashboard's child history uses, so a batch
// roster reads like the rest of the apartment module.
import * as styles from 'app/pages/Dashboard/ParentDashboard/components/StudentHistoryDrawer/styles'

/**
 * Who is enrolled in one batch, for a chosen month.
 *
 * An apartment enrolment is ONE ROW PER MONTH, so "the batch's children" only
 * means something for a month. The rows come from the building's enrolment
 * list (already carrying `batchId` and the row's own date range) and are
 * narrowed here, so this needs no endpoint of its own.
 */

// Statuses that hold the seat for the month the row covers. `lapsed` is in
// here because last month's row necessarily reads `lapsed` today — mirrors
// ENROLMENT_HELD_SEAT_STATUSES in oh_apartments/services/enrolment.py.
// `upcoming` is a booked month that has not started yet.
const HOLDS_SEAT = ['active', 'due', 'paused', 'lapsed', 'upcoming']

const monthKey = (date) => dayjs(date).format('YYYY-MM')

// Does [start, end] overlap the calendar month `key`? A null end runs on.
const overlapsMonth = (row, key) => {
  const first = dayjs(`${key}-01`)
  const last = first.endOf('month')
  const start = row.startDate ? dayjs(row.startDate) : null
  const end = row.endDate ? dayjs(row.endDate) : null
  if (start && start.isAfter(last, 'day')) return false
  if (end && end.isBefore(first, 'day')) return false
  return true
}

const RosterItem = ({ item }) => {
  const status = (item.status || '').toLowerCase()
  return (
    <styles.Item>
      <styles.ItemTitle>
        <span>{item.childName || '—'}</span>
        <styles.StatusPill $status={status}>{status || '—'}</styles.StatusPill>
      </styles.ItemTitle>
      <styles.ItemMeta>
        <span>{item.parentName || '—'}</span>
        {item.phone ? (
          <>
            <span>·</span>
            <span>{item.phone}</span>
          </>
        ) : null}
        <span>·</span>
        <span>{item.frequency || '—'}</span>
        <span>·</span>
        <span>
          {item.startDate ? dayjs(item.startDate).format('D MMM YY') : '—'}
          {item.endDate ? ` → ${dayjs(item.endDate).format('D MMM YY')}` : ''}
        </span>
      </styles.ItemMeta>
    </styles.Item>
  )
}

const BatchRosterDrawer = ({ open, onClose, batch, apartmentId }) => {
  const [rows, setRows] = React.useState(null)
  const [month, setMonth] = React.useState('')

  const batchId = batch?.uuid

  React.useEffect(() => {
    if (!open || !batchId) return undefined
    let cancelled = false
    setRows(null)
    fetchApartmentEnrolments(apartmentId)
      .then(
        (all) => !cancelled && setRows(all.filter((r) => r.batchId === batchId))
      )
      .catch(() => !cancelled && setRows([]))
    return () => {
      cancelled = true
    }
  }, [open, batchId, apartmentId])

  // Months this batch has rows in, newest first.
  const months = React.useMemo(() => {
    const keys = new Set(
      (rows || []).filter((r) => r.startDate).map((r) => monthKey(r.startDate))
    )
    return [...keys].sort().reverse()
  }, [rows])

  // Open on this month when the batch has it, else its newest month.
  React.useEffect(() => {
    if (!months.length) return
    const now = monthKey(new Date())
    setMonth(months.includes(now) ? now : months[0])
  }, [months])

  const inMonth = (rows || [])
    .filter((r) => month && overlapsMonth(r, month))
    .sort((a, b) => (a.childName || '').localeCompare(b.childName || ''))
  const holding = inMonth.filter((r) =>
    HOLDS_SEAT.includes((r.status || '').toLowerCase())
  )
  const dropped = inMonth.filter((r) => !holding.includes(r))

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <styles.DrawerInner>
        <styles.Header>
          <div>
            <styles.HeaderTitle>
              {batch?.subjectName || batch?.programName || 'Batch'}
            </styles.HeaderTitle>
            <styles.HeaderSub>
              {[batch?.displayName, batch?.teacherName]
                .filter(Boolean)
                .join(' · ')}
            </styles.HeaderSub>
          </div>
          <IconButton size="small" onClick={onClose} aria-label="close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </styles.Header>
        <styles.Body>
          {rows === null ? (
            <styles.Loading>loading…</styles.Loading>
          ) : months.length === 0 ? (
            <styles.Empty>no enrolments in this batch yet</styles.Empty>
          ) : (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 12
                }}
              >
                <Select
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                  variant="outlined"
                  margin="dense"
                >
                  {months.map((m) => (
                    <MenuItem key={m} value={m}>
                      {dayjs(`${m}-01`).format('MMM YYYY')}
                    </MenuItem>
                  ))}
                </Select>
                <span>
                  {holding.length} enrolled
                  {dropped.length ? ` · ${dropped.length} dropped` : ''}
                </span>
              </div>
              {inMonth.length === 0 ? (
                <styles.Empty>no enrolments in this month</styles.Empty>
              ) : (
                [...holding, ...dropped].map((it) => (
                  <RosterItem key={it.uuid} item={it} />
                ))
              )}
            </>
          )}
        </styles.Body>
      </styles.DrawerInner>
    </Drawer>
  )
}

export default BatchRosterDrawer
